import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { ToggleManufacturerInput } from '@deltek/specification-client/core/Models/toggleManufacturerInput'
import { ToggleImportedManufacturerInput } from 'api/graphQL/types/projectElement.types'
import { WorkspaceManufacturerSelected } from 'shared/utils/workspaceUtils'
import { HyperlinkParentNode } from '../features/hyperlinkSelection/types'
import { getImportTypeInfo } from './importTypeUtils'

interface HyperlinkMutationParams {
    projectId: string
    selectedProjectElementNode: ProjectElementNode | null
    familyAddedProductTypes: ProjectElementNode[]
    parentNode: HyperlinkParentNode | null
    currentSelectedItem: Partial<TextContentElement>
    isSelected: boolean
}

/**
 * Determines if this is an imported/spectext node
 */
export const isImportedProductType = (node: ProjectElementNode | null): boolean => {
    if (!node) return false

    const { isOMImport, isMasterSpecClone } = getImportTypeInfo(node)

    // Masterspec clones should NOT be considered imported
    if (isMasterSpecClone) return false

    return Boolean(node.isImportedToProject || isOMImport || node.isSpecTextNode)
}

/**
 * Determines the nugget ID based on context (family view vs regular view)
 */
const getNuggetId = (params: HyperlinkMutationParams): string => {
    const {
        selectedProjectElementNode,
        parentNode,
        familyAddedProductTypes,
    } = params

    // Check if this is a family view scenario
    const isFamilyView =
        selectedProjectElementNode?.elementType === 'PRODUCTFAMILY'

    if (
        isFamilyView &&
        parentNode?.dtvContentItemParentId &&
        familyAddedProductTypes?.length
    ) {
        // In family view, find the parent product type that matches
        const parentProductType = familyAddedProductTypes.find(
            (pt) => pt.baseElementId === parentNode.dtvParentId
        )
        if (parentProductType) {
            return parentProductType.elementId || ''
        }
    } else if (selectedProjectElementNode?.elementType === 'PRODUCTTYPE') {
        // Regular product type scenario
        return selectedProjectElementNode.elementId || ''
    }

    return ''
}

/**
 * Creates input for regular manufacturer selection mutation
 */
const createRegularInput = (
    params: HyperlinkMutationParams
): Partial<ToggleManufacturerInput> => {
    const { projectId, parentNode, currentSelectedItem, isSelected } = params
    const listingId = parentNode?.pillId || currentSelectedItem.id || ''
    const productFamilyId = parentNode?.productFamilyId || ''

    // Type-safe way to get firmName
    const itemWithFirmName = currentSelectedItem as TextContentElement & {
        firmName?: string
    }
    const firmName = itemWithFirmName.firmName || currentSelectedItem.text || ''

    return {
        isBOD: false,
        isSelected,
        listingId,
        productFamilyId,
        projectId: projectId || '',
        projectNuggetId: getNuggetId(params) || '',
        isRetainProperties: false,
        firmName,
        type: 'MANUFACTURER' as ListingLevel,
    }
}

/**
 * Creates input for imported product type manufacturer selection mutation
 */
const createImportedInput = (
    params: HyperlinkMutationParams
): Partial<ToggleImportedManufacturerInput> => {
    const {
        projectId,
        selectedProjectElementNode,
        parentNode,
        currentSelectedItem,
        isSelected,
    } = params
    const manufacturerContentItemId =
        parentNode?.pillId || currentSelectedItem.id || ''
    const productFamilyId = parentNode?.productFamilyId || ''

    return {
        isBOD: false,
        isSelected,
        productFamilyId,
        projectId: projectId || '',
        projectNuggetId: getNuggetId(params) || '',
        manufacturerContentItemId,
        isMasterSpecCloneNode: selectedProjectElementNode?.isMasterSpecClone,
        iterationId: selectedProjectElementNode?.iteration?.id,
    }
}

/**
 * Creates the appropriate mutation input based on node type
 */
export const createHyperlinkMutationInput = (
    params: HyperlinkMutationParams
): {
    isImported: boolean
    input:
        | Partial<ToggleManufacturerInput>
        | Partial<ToggleImportedManufacturerInput>
} => {
    const isImported = isImportedProductType(params.selectedProjectElementNode)
 
    return {
        isImported,
        input: isImported
            ? createImportedInput(params)
            : createRegularInput(params),
    }
}

/**
 * Creates service worker message data
 */
export const createServiceWorkerMessage = (
    params: HyperlinkMutationParams
): WorkspaceManufacturerSelected => {
    const { parentNode, currentSelectedItem, isSelected } = params
    const listingId = parentNode?.pillId || currentSelectedItem.id || ''

    return {
        listingId,
        isSelected,
        isBOD: false,
        hasSupportedManufacturersOptions: false,
        listingText: currentSelectedItem.text || '',
        elementId: getNuggetId(params) || '',
        isFromDialogMutation: true,
    } as WorkspaceManufacturerSelected
}
